import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../utils";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import * as React from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center font-semibold transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-60 active:translate-y-[1px] active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-white shadow-[var(--btn-shadow,0_12px_30px_rgba(0,0,0,0.08))] hover:bg-accent hover:shadow-xl hover:-translate-y-[2px] hover:scale-105 focus-visible:ring-primary dark:bg-[color:var(--color-primary,#7cc4ff)] dark:text-[#0b1220] dark:hover:bg-[color:var(--color-accent,#ffb347)]",
        secondary:
          "bg-secondary text-white shadow-[var(--btn-shadow,0_12px_30px_rgba(0,0,0,0.08))] hover:bg-primary hover:shadow-xl hover:-translate-y-[2px] hover:scale-105 focus-visible:ring-secondary dark:bg-[color:var(--color-secondary,#ff6baf)] dark:text-white dark:hover:bg-[color:var(--color-primary,#7cc4ff)]",
        outline:
          "border-2 border-[color:var(--color-primary,#2563eb)] text-[color:var(--color-primary,#2563eb)] bg-[var(--color-surface,#fff)] hover:bg-primary hover:text-white hover:-translate-y-[2px] hover:scale-105 focus-visible:ring-primary dark:border-[color:var(--color-primary,#7cc4ff)] dark:text-[color:var(--color-primary,#7cc4ff)] dark:hover:bg-[color:var(--color-primary,#7cc4ff)] dark:hover:text-[#0b1220]",
        ghost:
          "text-[color:var(--color-primary,#2563eb)] hover:bg-[color:var(--color-primary,#2563eb)]/10 hover:scale-105 focus-visible:ring-primary/40 dark:text-[color:var(--color-primary,#7cc4ff)] dark:hover:bg-[color:var(--color-primary,#7cc4ff)]/12",
        soft:
          "bg-[color:var(--color-primary,#2563eb)]/12 text-[color:var(--color-primary,#2563eb)] border border-[color:var(--color-primary,#2563eb)]/10 hover:bg-[color:var(--color-primary,#2563eb)]/25 hover:border-[color:var(--color-primary,#2563eb)]/25 hover:scale-105 focus-visible:ring-primary/30 dark:bg-[color:var(--color-primary,#7cc4ff)]/18 dark:text-[color:var(--color-primary,#7cc4ff)] dark:border-[color:var(--color-primary,#7cc4ff)]/18 dark:hover:bg-[color:var(--color-primary,#7cc4ff)]/30",
      },
      size: {
        sm: "h-9 px-3 text-sm rounded-[var(--radius-button,0.5rem)]",
        md: "h-10 px-4 text-base rounded-[var(--radius-button,0.5rem)]",
        lg: "h-12 px-6 text-lg rounded-[var(--radius-button,0.75rem)]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: ReactNode;
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  /** Enable subtle ripple micro-interaction on press */
  ripple?: boolean;
}

export function Button({
  className,
  variant,
  size,
  children,
  asChild = false,
  loading = false,
  loadingText,
  ripple = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  const baseClass = cn(
    buttonVariants({ variant, size, className }),
    "relative overflow-hidden sf-pressable",
    ripple && "sf-ripple"
  );
  const isDisabled = props.disabled || loading;

  // When asChild is true, Slot requires a single element child; avoid injecting extra nodes.
  if (asChild) {
    const child = React.isValidElement(children) ? children : <span>{children}</span>;
    return (
      <Comp
        className={baseClass}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        {...props}
      >
        {child}
      </Comp>
    );
  }

  const content = React.isValidElement(children) ? children : <span>{children}</span>;

  return (
    <Comp
      className={baseClass}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" />
          {loadingText && <span className="ml-2 text-sm">{loadingText}</span>}
        </span>
      )}
      <span className={cn("flex items-center gap-2 transition-opacity", loading && "opacity-0")}>
        {content}
      </span>
    </Comp>
  );
}
